import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import Autosuggest from "react-autosuggest";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import throttle from "lodash/throttle";
import { Search } from "react-feather";

import { palette, mediaMin } from "../ui-components";

const autocompleteService = { current: null };

const propTypes = {
  onSelection: PropTypes.func.isRequired,
};

/**
 * Renders the AutoComplete component
 *
 * @component
 * @param {func} onSelection
 */
const AutoComplete = ({ onSelection }) => {
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);

  const fetch = useMemo(
    () =>
      throttle((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 200),
    []
  );

  useEffect(() => {
    let active = true;

    if (!autocompleteService.current && window.google)
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    if (!autocompleteService.current) return undefined;
    if (inputValue === "") return undefined;

    fetch({ input: inputValue }, (results) => {
      if (active) {
        const newOptions = results ? [...results] : [];
        setOptions(newOptions);
      }
    });

    return () => (active = false);
  }, [inputValue, fetch]);

  const onKeyDown = (event) => {
    if (event.keyCode === 13 && options.length > 0) {
      onSelection(options[0]);
      setInputValue(options[0].description);
    }
  };

  return (
    <Wrapper>
      <Autosuggest
        suggestions={options}
        onSuggestionsClearRequested={() => setOptions([])}
        onSuggestionsFetchRequested={({ value }) => setInputValue(value)}
        getSuggestionValue={(option) => {
          onSelection(option);
          return option.description;
        }}
        renderSuggestion={(option) => {
          const matches = match(option.description, inputValue);
          const parts = parse(option.description, matches);
          return parts.map((part, index) => (
            <span
              key={index}
              style={{ fontWeight: part.highlight ? 700 : 300 }}
            >
              {part.text}
            </span>
          ));
        }}
        inputProps={{
          placeholder: "Search by location",
          value: inputValue,
          onChange: (_, { newValue }) => setInputValue(newValue),
          onKeyDown: onKeyDown,
        }}
        focusInputOnSuggestionClick={false}
      />
      <SearchIconContainer>
        <Search width={20} />
      </SearchIconContainer>
    </Wrapper>
  );
};

AutoComplete.propTypes = propTypes;

export { AutoComplete };

const Wrapper = styled.div`
  width: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  border-radius: 15px;
  background-color: ${palette.white};
  padding: 0 5px 0 10px;

  input {
    color: ${palette.dark};
    height: 32px;
    font-size: 13px;
    font-family: Poppins, sans-serif;
    border: none;
    margin: 0;
    padding: 0;
    width: 100%;

    &::placeholder {
      color: ${palette.dark} !important;
      font-weight: 500;
      opacity: 1;
    }

    ${mediaMin.desktop_sm`
      font-size: 14px;
      min-width: 250px;
    `};
  }

  .react-autosuggest__suggestions-container {
    background-color: ${palette.tan};
    position: absolute;
    border-radius: 0 0 15px 15px;
    left: 0;
    right: 0;
    top: 48px;
    box-shadow: 0px 3px 3px 0px rgba(0, 0, 0, 0.4);
    min-width: 300px;

    ${mediaMin.tablet_sm`
      min-width: 350px;
    `};
  }

  .react-autosuggest__suggestions-list {
    list-style: none;
    padding: 0;
    margin: 10px;
  }

  .react-autosuggest__suggestion {
    padding: 10px 10px 10px 30px;
    border-top: 1px solid ${palette.green};
    background-image: url("https://mappie.io/build/images/pin-around-me.png");
    background-repeat: no-repeat;
    background-position: 2px;
    font-size: 14px;
    background-size: 24px;
    line-height: 20px;

    &.react-autosuggest__suggestion--first {
      border-top: none;
    }
  }
`;

const SearchIconContainer = styled.div`
  padding-left: 5px;
`;
